import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLoading } from 'hooks/useLoading';
import IItinerary from 'interfaces/IItinerary';
import ItineraryService from 'services/ItineraryService';
import ItineraryCategoryService from 'services/ItineraryCategoryService';
import Heading from 'components/Heading';
import CategoryTable from 'components/CategoryTable';
import ConfirmationModal from 'components/ConfirmationModal';
import Button from 'components/Button';
import editIcon from 'assets/images/editIcon.svg';
import trashIcon from 'assets/images/trashIcon.svg';
import { Container } from './styles';
import { DataGrid, GridColDef, GridToolbar, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { Grid } from '@mui/material';

const Itinerary = (): JSX.Element => {
  const { openLoading, closeLoading } = useLoading();
  const [itineraries, setItineraries] = useState<IItinerary[]>([]);
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [itineraryId, setItineraryId] = useState(0);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    id: false,
  });

  const history = useHistory();
  const service = new ItineraryService();

  useEffect(() => {
    openLoading();
    fetchItineraries().finally(() => closeLoading());
  }, []);

  const fetchItineraries = async () => {
    try {
      const data = await service.findAll();
      setItineraries(data);
    } catch (error) {
      console.error('Erro ao buscar roteiros:', error);
    }
  };

  const deleteItinerary = async () => {
    openLoading();
    try {
      await service.remove(itineraryId);
      setItineraries(prev => prev.filter(item => item.id !== itineraryId));
    } finally {
      closeLoading();
    }
  };

  const formatDateTime = (dateString: string): JSX.Element => {
    if (!dateString) return <span>- - -</span>;
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('pt-BR');
    const formattedTime = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return (
      <div className="createdDate">
        <span>{formattedDate}</span>
        <span>{formattedTime}</span>
      </div>
    );
  };

  const daysOfWeek = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const formatOpeningHours = (hours: string[]) => {
    if (!hours || hours.length === 0) return [];
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const groups: { range: string; time: string }[] = [];

    let startIdx = 0;
    for (let i = 1; i <= hours.length; i++) {
      if (i === hours.length || hours[i] !== hours[startIdx]) {
        const range = i - 1 === startIdx ? days[startIdx] : `${days[startIdx]} a ${days[i - 1]}`;

        groups.push({ range, time: hours[startIdx] });
        startIdx = i;
      }
    }
    return groups;
  };

  const itineraryColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.5,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'createdAt',
      headerName: 'Criação',
      flex: 1.5,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => formatDateTime(params.value as string),
    },
    {
      field: 'updatedAt',
      headerName: 'Última Edição',
      flex: 1.5,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => formatDateTime(params.value as string),
    },
    {
      field: 'user',
      headerName: 'Cadastrado/Editado Por',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
      valueGetter: params => params.row.user?.name || params.row.user?.email || '- - -',
    },
    {
      field: 'title',
      headerName: 'Título do Roteiro',
      flex: 3,
      minWidth: 300,
      align: 'left',
      headerAlign: 'center',
    },
    {
      field: 'category',
      headerName: 'Categoria',
      flex: 3,
      minWidth: 300,
      align: 'left',
      headerAlign: 'center',
    },
    {
      field: 'openingHoursDetail',
      headerName: 'Horários de Funcionamento',
      flex: 3,
      minWidth: 450,
      renderCell: params => {
        const hours = params.value as string[];
        if (!hours || !Array.isArray(hours)) return <span style={{ color: '#999' }}>Não informado</span>;

        const groupedHours = formatOpeningHours(hours);

        return (
          <div
            style={{
              display: 'flex',
              gap: '6px',
              flexWrap: 'wrap',
              alignItems: 'center',
              padding: '4px 0',
            }}>
            {groupedHours.map((group, index) => {
              const isClosed = group.time.toLowerCase() === 'fechado';
              return (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                  }}>
                  <span style={{ fontWeight: 700, color: '#4a5568', marginRight: '5px' }}>{group.range}:</span>
                  <span
                    style={{
                      fontWeight: 600,
                      color: isClosed ? '#c53030' : '#166534',
                    }}>
                    {group.time}
                  </span>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      flex: 1,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="button-icon" title="Editar" onClick={() => history.push(`/itinerary/${params.id}`)}>
            <img src={editIcon} alt="editar" />
          </button>

          <button
            className="button-icon"
            title="Excluir"
            onClick={() => {
              setItineraryId(Number(params.id));
              setConfirmDeletion(true);
            }}>
            <img src={trashIcon} alt="excluir" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Container>
      <CategoryTable title="Categorias de Roteiros" service={new ItineraryCategoryService()} />
      <div style={{ marginTop: '2rem' }}>
        <div
          className="header"
          style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center' }}>
          <Heading>Lista de Roteiros Cadastrados pela SETUR</Heading>
          <Button type="submit" buttonType="primary" onClick={() => history.push('/itinerary')}>
            Adicionar
          </Button>
        </div>

        <Grid sx={{ width: '100%', marginBottom: '50px' }}>
          <DataGrid
            sx={{
              background: '#fff',
              boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
              borderRadius: '18px',
              width: '100%',
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
              },
            }}
            rows={itineraries}
            columns={itineraryColumns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                densitySelector: false,
                exportButton: false,
              },
            }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Grid>
      </div>
      <ConfirmationModal
        open={confirmDeletion}
        title="EXCLUSÃO DE ITEM"
        message="Você tem certeza de que deseja excluir esse item?"
        onCancel={() => setConfirmDeletion(false)}
        onConfirm={() => {
          setConfirmDeletion(false);
          deleteItinerary();
        }}
      />
    </Container>
  );
};

export default Itinerary;
